import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Dialog} from 'primereact/dialog';
import {AdminButton, AdminInputDropdown} from '../../../layout/admin';
import {Collection} from '../../publications/types/Collection';
import collectionsService from '../../publications/services/collectionsService';
import {handleError} from '../../../common/utils/errorHandling';
import {getValueWithFallback} from '../../../common/types/StringTranslations';

interface AddToCollectionDialogProps {
  visible: boolean;
  onHide: () => void;
  artisticObjectId: string;
  artisticObjectName: string;
}

const AddToCollectionDialog: React.FC<AddToCollectionDialogProps> = ({
  visible,
  onHide,
  artisticObjectId,
  artisticObjectName
}) => {
  const {t, i18n} = useTranslation();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (visible) {
      fetchCollections();
    }
  }, [visible]);

  const fetchCollections = async (): Promise<void> => {
    await handleError(
      async () => {
        const collectionsData = await collectionsService.getAllCollections();
        setCollections(collectionsData);
      },
      t('admin.artisticObjects.addToCollection.errors.fetching')
    );
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    await handleError(
      async () => {
        await collectionsService.addItemToCollection(selectedCollectionId, artisticObjectId);
        console.log(`"${artisticObjectName}" added to collection successfully!`);
        handleDialogHide();
      },
      t('admin.artisticObjects.addToCollection.errors.adding'),
      () => alert(t('admin.artisticObjects.addToCollection.errors.adding'))
    );

    setLoading(false);
  };

  const handleDialogHide = (): void => {
    setSelectedCollectionId('');
    onHide();
  };

  const collectionOptions = collections.map(collection => ({
    label: getValueWithFallback(collection.title, i18n.language),
    value: collection.id
  }));

  return (
    <Dialog
      header={t('admin.artisticObjects.addToCollection.dialogTitle')}
      visible={visible}
      style={{width: '50vw'}}
      onHide={handleDialogHide}
      maskClassName="bg-black bg-opacity-50"
    >
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <p
            className="text-gray-700"
            dangerouslySetInnerHTML={{
              __html: t('admin.artisticObjects.addToCollection.description', {name: artisticObjectName})
            }}
          />
        </div>

        <AdminInputDropdown
          value={selectedCollectionId}
          onChange={(e) => setSelectedCollectionId(e.target.value)}
          name="collectionId"
          id="collectionId"
          label={t('admin.artisticObjects.addToCollection.collectionLabel')}
          options={collectionOptions}
          placeholder={t('admin.artisticObjects.addToCollection.selectCollection')}
          required
        />

        <div className="flex justify-end gap-4 pt-4">
          <AdminButton
            type="button"
            label={t('admin.common.actions.cancel')}
            onClick={handleDialogHide}
            variant="white"
          />
          <AdminButton
            type="submit"
            label={t('admin.common.actions.add')}
            variant="black"
            disabled={loading || !selectedCollectionId}
          />
        </div>
      </form>
    </Dialog>
  );
};

export default AddToCollectionDialog;
